import { useEffect, useState } from 'react';
import axios from 'axios';
import { Rating } from 'react-simple-star-rating';
import {
  StarOutline,
  CategoryOutlined,
  PersonOutlined,
  SearchOutlined,
  FilterListOutlined,
  TrendingUp,
  BarChartOutlined,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

function Ratings() {
  const server_url = process.env.REACT_APP_API_URI;
  const [category, setCategory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [ratings, setRatings] = useState([]);
  const [filteredRatings, setFilteredRatings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRatings, setTotalRatings] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [stats, setStats] = useState({
    totalRatings: 0,
    averageRating: 0,
    totalCategories: 0,
    totalUsers: 0,
  });
  const limit = 10;

  useEffect(() => {
    const getAllCategory = async () => {
      try {
        const url = `${server_url}/context/get-all-category`;
        const response = await axios.get(url);
        const categories = response.data.data;
        setCategory(categories);
        setStats((prev) => ({ ...prev, totalCategories: categories.length }));
      } catch (err) {
        console.log(err);
      }
    };
    getAllCategory();
  }, [server_url]);

  useEffect(() => {
    let filtered = [...ratings];

    if (searchTerm) {
      filtered = filtered.filter((rating) =>
        rating?.userId?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredRatings(filtered);
  }, [searchTerm, ratings]);

  const getAllRatings = async (cat_id, page = 1) => {
    try {
      setLoading(true);
      const url = `${server_url}/fetchRatingsByCatId/${cat_id}?page=${page}&limit=${limit}`;
      const response = await axios.get(url);
      const ratingsData = response.data.data;
      setRatings(ratingsData);
      setFilteredRatings(ratingsData);
      setTotalPages(response.data.totalPages || 1);
      setTotalRatings(response.data.total || ratingsData.length);

      // Calculate average rating
      const avgRating = calculateAverageRating(ratingsData);
      setStats((prev) => ({
        ...prev,
        totalRatings: response.data.total || ratingsData.length,
        averageRating: avgRating,
        totalUsers: new Set(ratingsData.map((r) => r.userId)).size,
      }));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageRating = (ratingsData) => {
    if (!ratingsData || ratingsData.length === 0) return 0;

    let totalRating = 0;
    let ratingCount = 0;

    ratingsData.forEach((row) => {
      row.ratings?.forEach((item) => {
        if (typeof item === 'object') {
          Object.values(item).forEach((ratingData) => {
            if (ratingData?.rating) {
              totalRating += parseFloat(ratingData.rating);
              ratingCount++;
            }
          });
        }
      });
    });

    return ratingCount > 0 ? (totalRating / ratingCount).toFixed(2) : 0;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getAllRatings(selectedCategory, page);
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Ratings Management
        </h1>
        <p className="text-gray-600">
          View and analyze user ratings across different categories
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Ratings</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalRatings}
              </h3>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <StarOutline className="text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Average Rating
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.averageRating}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <BarChartOutlined className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Categories</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalCategories}
              </h3>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <CategoryOutlined className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Unique Users</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalUsers}
              </h3>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <PersonOutlined className="text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Category Selector */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FilterListOutlined
                className="text-gray-500 mr-1"
                fontSize="small"
              />
              Select Category
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              value={selectedCategory}
              onChange={(e) => {
                const selectedId = e.target.value;
                setSelectedCategory(selectedId);
                setCurrentPage(1);
                setSearchTerm('');
                getAllRatings(selectedId, 1);

                const selectedCat = category.find(
                  (cat) => cat._id === selectedId,
                );
                if (selectedCat) {
                  setSelectedCategoryName(selectedCat.title);
                }
              }}
            >
              <option value="">Choose a category to view ratings</option>
              {category?.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      {selectedCategory && ratings.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Ratings Table */}
      {selectedCategory && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <tr>
                    <th className="py-4 px-6 text-left font-semibold">User</th>
                    <th className="py-4 px-6 text-left font-semibold">
                      Context
                    </th>
                    <th className="py-4 px-6 text-left font-semibold">
                      Ratings
                    </th>
                    <th className="py-4 px-6 text-center font-semibold w-20">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRatings?.length > 0 ? (
                    filteredRatings.map((row) => (
                      <tr
                        key={row._id}
                        className="hover:bg-blue-50 transition-colors"
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                              {row.userId?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <span className="text-gray-800 font-medium text-sm">
                              {row.userId}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div
                            className={`space-y-1 ${expandedRows[row._id] ? '' : 'max-h-20 overflow-hidden'}`}
                          >
                            {row.context?.map((item, index) => (
                              <div key={index} className="text-sm">
                                {typeof item === 'object' ? (
                                  Object.entries(item).map(([key, value]) => (
                                    <p key={key} className="text-gray-700">
                                      <span className="font-semibold text-gray-900">
                                        {key}:
                                      </span>{' '}
                                      {value}
                                    </p>
                                  ))
                                ) : (
                                  <p className="text-gray-700">{item}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div
                            className={`space-y-2 ${expandedRows[row._id] ? '' : 'max-h-20 overflow-hidden'}`}
                          >
                            {row.ratings?.map((item, index) => (
                              <div key={index}>
                                {typeof item === 'object' ? (
                                  Object.entries(item).map(
                                    ([_, ratingData]) => (
                                      <div
                                        key={ratingData?.questionTitle}
                                        className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2"
                                      >
                                        <span className="font-semibold text-sm text-gray-900">
                                          {ratingData?.questionTitle}:
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm text-gray-600 font-medium">
                                            {ratingData?.rating}
                                          </span>
                                          <Rating
                                            readonly
                                            initialValue={
                                              ratingData?.rating || 1
                                            }
                                            allowFraction
                                            size={18}
                                            emptyColor="#D1D5DB"
                                            fillColor="#FBBF24"
                                            iconsCount={5}
                                          />
                                        </div>
                                      </div>
                                    ),
                                  )
                                ) : (
                                  <p className="text-sm text-gray-700">
                                    {item}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <button
                            onClick={() => toggleRowExpansion(row._id)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title={
                              expandedRows[row._id] ? 'Collapse' : 'Expand'
                            }
                          >
                            {expandedRows[row._id] ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <StarOutline className="text-gray-400 text-5xl" />
                          <p className="text-gray-500 text-lg font-medium">
                            {searchTerm
                              ? 'No ratings match your search'
                              : 'No ratings found'}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {searchTerm
                              ? 'Try adjusting your search term'
                              : 'No users have submitted ratings for this category yet'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Empty State - No Category Selected */}
      {!selectedCategory && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <CategoryOutlined className="text-gray-400 text-6xl mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Select a Category
          </h3>
          <p className="text-gray-500">
            Choose a category from the dropdown above to view ratings
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && selectedCategory && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-2 overflow-x-auto">
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }

              return (
                <button
                  key={index}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Ratings;
